/**
 * Cola de revisión — la mesa de trabajo (cola.html): todo lo que espera una
 * decisión humana, por carriles P1/P2, con evidencia y consecuencia a la
 * vista y atajos A/E/R sobre la tarjeta enfocada (los dispara HitlCard).
 * Las 7 tarjetas SON COLA_SEED (cola.seed.tsx) — el motor que las detecta
 * (insights, activaciones, precedencias, retracciones) llega con F5b; hoy
 * son datos fijos. Resolver actúa en local, como el prototipo: la tarjeta
 * sale con su transición de salida y un toast, y pasa al rastro de
 * «Resueltas hoy» (aside propio de la página,
 * vía useAsideSlot — la sidepane compartida de la cola NO se muestra aquí,
 * la página ES la cola).
 * Estados: lista / vacío / cargando / error, forzables con ?estado= (no
 * hay aún consulta real que los dispare — F5b).
 */
import { useState, type ReactNode } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Badge, Banner, Button, Card, Empty, Fainted, Feed, FeedItem, FeedWho, HitlCard, Kbd,
  PageHead, QLane, Select, SearchInput, Sidepane, Skeleton, Stale, Toolbar, useSlashFocus, useToast,
} from "@skein/ui";
import { buildActions, COLA_SEED, type QueueCard, type QueueLane, type QueueTipo } from "./cola.seed";
import { useAsideSlot } from "../../shell/asideSlot";

const TIPOS: ReadonlyArray<{ value: "" | QueueTipo; label: ReactNode }> = [
  { value: "", label: "Todos los tipos" },
  { value: "insight", label: "◈ insights" },
  { value: "activacion", label: "⌘ activaciones" },
  { value: "precedencia", label: "§ precedencias" },
  { value: "retraccion", label: "△ retracciones" },
];

/** SEMILLA del rastro de hoy (cola.html) — el rastro real lo escribe cada resolución (propia y ajena) cuando exista el motor (F5b). */
const RESUELTAS_SEED: ReactNode[] = [
  <FeedItem key="r1" t="11:52"><FeedWho>n.serra</FeedWho> aplicó el insight <b>glosa del punto muerto</b> al draft de <b>umbral-rentabilidad</b></FeedItem>,
  <FeedItem key="r2" t="10:15"><FeedWho>e.puig</FeedWho> registró la activación <b>BC · Talleres Comas</b></FeedItem>,
  <FeedItem key="r3" t="09:03"><FeedWho>n.serra</FeedWho> descartó un insight de <b>rotacion-stock</b> — motivo en auditoría</FeedItem>,
];

function SkeletonMesa() {
  const card = (kW: string, l1: string, l2: string | undefined, actW: string) => (
    <Card>
      <Skeleton style={{ width: kW, height: ".7em", marginBottom: "var(--sk-sp-3)" }} />
      <Skeleton style={{ height: ".95em", width: l1 }} />
      {l2 && <Skeleton style={{ height: ".8em", width: l2, marginTop: "var(--sk-sp-2)" }} />}
      <Skeleton style={{ height: "1.6em", width: actW, marginTop: "var(--sk-sp-3)" }} />
    </Card>
  );
  return (
    <div className="mesa">
      {card("9rem", "85%", "70%", "14rem")}
      {card("11rem", "75%", "60%", "11rem")}
      {card("8rem", "80%", undefined, "12rem")}
    </div>
  );
}

/** Carriles P1/P2 con `render` para cada tarjeta — comparte el orden con el prototipo. */
function Carriles({ cards, render }: { cards: readonly QueueCard[]; render: (c: QueueCard) => ReactNode }) {
  const lane = (l: QueueLane) => cards.filter((c) => c.lane === l);
  return (
    <>
      {lane("p1").length > 0 && <QLane>hoy</QLane>}
      {lane("p1").map(render)}
      {lane("p2").length > 0 && <QLane p={2}>esta semana</QLane>}
      {lane("p2").map(render)}
    </>
  );
}

export function ColaPage() {
  const say = useToast();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { estado?: string };
  const inputRef = useSlashFocus();
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState<"" | QueueTipo>("");
  const [gone, setGone] = useState<Set<string>>(new Set());
  const [left, setLeft] = useState<Set<string>>(new Set());
  const [resueltas, setResueltas] = useState<ReactNode[]>(RESUELTAS_SEED);

  const resolve = (c: QueueCard, done: string, past: string) => {
    setGone((g) => new Set(g).add(c.id));
    say(done);
    window.setTimeout(() => {
      setLeft((l) => new Set(l).add(c.id));
      setResueltas((r) => [<FeedItem key={`${c.id}-${Date.now()}`} t="ahora"><FeedWho>n.serra</FeedWho> {past} {c.ent}</FeedItem>, ...r]);
    }, 280);
  };

  const vivos = COLA_SEED.filter((c) => !left.has(c.id));
  const query = q.trim().toLowerCase();
  const visibles = vivos.filter((c) =>
    (!query || c.titleText.includes(query) || (typeof c.kind === "string" && c.kind.toLowerCase().includes(query))) &&
    (!tipo || c.tipo === tipo));
  const lateCount = vivos.filter((c) => c.late).length;

  useAsideSlot(
    <Sidepane className="aside" aria-label="Resueltas hoy" title="Resueltas hoy" count={resueltas.length}>
      <Feed full clock>{resueltas}</Feed>
      <Empty solid inline title="Media de hoy: 1 h 40 m" style={{ marginTop: "var(--sk-sp-3)" }}>
        {" "}de espera por resolución — el SLA de insights es 8 h.
      </Empty>
    </Sidepane>,
  );

  const forced = search.estado;
  const view = forced === "vacio" || forced === "cargando" || forced === "error" ? forced : "lista";

  const renderCard = (c: QueueCard) => {
    const actions = buildActions(c, {
      resolve: (done, past) => resolve(c, done, past),
      navigate: (a) => void navigate({ to: a.to, params: a.params }),
      say,
    });
    return <HitlCard key={c.id} kind={c.kind} act={c.tipo === "activacion"} sla={c.sla} late={c.late} title={c.title} leaving={gone.has(c.id)} rows={c.rows} actions={actions} />;
  };
  // copia congelada del estado de error: misma lectura, sin foco ni acciones
  const renderStale = (c: QueueCard) => (
    <HitlCard key={c.id} kind={c.kind} act={c.tipo === "activacion"} sla={c.sla} late={c.late} title={c.title} rows={c.rows} actions={[]} interactive={false} />
  );

  return (
    <>
      <PageHead
        title="Cola de revisión"
        meta={view === "vacio" ? <Badge>0 pendientes</Badge>
          : view === "cargando" ? <Skeleton style={{ width: "18rem", height: "1.25em" }} />
          : <>
            <Badge>{visibles.length} pendientes</Badge>
            {lateCount > 0 && <Badge tone="danger">{lateCount} SLA vencido</Badge>}
            <Fainted style={{ fontSize: "var(--sk-fs-2xs)", alignSelf: "center" }}>nada llega a producción sin pasar por aquí</Fainted>
          </>}
        actions={<span className="keys" style={{ alignItems: "center", gap: "var(--sk-sp-2)" }}>
          <Fainted style={{ fontSize: "var(--sk-fs-2xs)" }}>sobre la tarjeta enfocada</Fainted><Kbd>A</Kbd><Kbd>E</Kbd><Kbd>R</Kbd>
        </span>}
      />

      {view === "lista" && vivos.length > 0 && (
        <div style={{ maxWidth: "46rem", marginBottom: "var(--sk-gap)" }}>
          <Toolbar>
            <SearchInput inputRef={inputRef} style={{ flex: 1, minWidth: "12rem" }} placeholder="Buscar en la cola…" aria-label="Buscar en la cola" shortcut="/" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select ariaLabel="Filtrar por tipo" style={{ width: "13rem" }} value={tipo} onChange={setTipo} options={TIPOS} />
          </Toolbar>
        </div>
      )}

      {view === "vacio" && (
        <Empty glyph="✳" title="Cola vacía" style={{ maxWidth: "46rem" }}>
          El taller respira — lo urgente está hecho y lo importante, curándose.
          Todo lo que el sistema proponga (insights, activaciones, conflictos) aparecerá aquí antes de tocar producción.
        </Empty>
      )}

      {view === "cargando" && <SkeletonMesa />}

      {view === "error" && (
        <>
          <Banner tone="danger" style={{ maxWidth: "46rem", marginBottom: "var(--sk-gap)" }}>
            <b>No llego a la cola.</b> Lo que ves puede estar desactualizado — no resuelvas nada hasta reconectar. Reintento solo cada 30 s.{" "}
            <Button variant="link" onClick={() => say("Reintentando la conexión…")}>reintentar ahora</Button>
          </Banner>
          <Stale stale className="mesa" inert>
            <Carriles cards={vivos} render={renderStale} />
          </Stale>
        </>
      )}

      {view === "lista" && vivos.length === 0 && (
        /* vaciar la cola resolviendo lleva al vacío zen (contrato: setEstado('vacio')) */
        <Empty glyph="✳" title="Cola vacía" style={{ maxWidth: "46rem" }}>
          El taller respira — lo urgente está hecho y lo importante, curándose.
          Todo lo que el sistema proponga (insights, activaciones, conflictos) aparecerá aquí antes de tocar producción.
        </Empty>
      )}
      {view === "lista" && vivos.length > 0 && (
        <div className="mesa">
          {visibles.length === 0
            ? <Empty inline title="Nada casa con ese filtro.">Prueba a quitar el tipo.</Empty>
            : <Carriles cards={visibles} render={renderCard} />}
        </div>
      )}
    </>
  );
}
